import { z } from 'zod'

import {
  EnumBuilder,
  type NonEmptyArray,
  type ZodSimplify,
} from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  BidScoreSchema,
  BidScorecardModelSchema,
} from './bid-scoring.models.ts'
import {
  CostMarginPercentSchema,
  DollarsSchema,
  NonEmptyStringSchema,
  TimestampSchema,
} from './property.models.ts'
import {
  AgreementIdSchema,
  BidIdSchema,
  BidUrnSchema,
  OrganizationIdSchema,
  ProjectIdSchema,
  TeamIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * BidStatus
 */
export const [BidStatuses, BidStatusSchema, BidStatusEnum] = EnumBuilder(
  'declined', // The agency has indicated they do not want to submit a bid
  'draft', // The agency is preparing the bid but has not yet submitted it
  'submitted', // The bid has been officially submitted for consideration
  'disqualified', // The bid was not submitted on time
  'accepted', // The bid was accepted and the contract is being finalized
  'rejected', // The bids that were specifically rejected by the brand
  'unsuccessful' // The bids that were not specifically rejected, but did not win the bid
)
export type BidStatus = ZodSimplify<typeof BidStatusSchema>

export const BrandBidStatuses: NonEmptyArray<BidStatus> = [
  'accepted',
  'rejected',
  'submitted',
]

/**
 * Postgres max value for integer is 2147483647 which we store as cents
 * The max amount of dollars that can be represented is $21,474.836.47
 * The amountPlusMargin is twice as large as the amount, so we're staying about half this size to be safe.
 */
export const BidAmountMax = 10_000_000
export const BidAmountSchema = DollarsSchema(BidAmountMax)

/**
 * BidDomainSelectModel
 */
export const BidDomainSelectModelSchema = z
  .object({
    bidId: BidIdSchema,
    bidUrn: BidUrnSchema,
    projectId: ProjectIdSchema,
    agencyOrganizationId: OrganizationIdSchema,
    agencyTeamId: TeamIdSchema,
    status: BidStatusSchema,
    revision: z.number().int().nonnegative(),
    amount: BidAmountSchema,
    costMarginPercent: CostMarginPercentSchema,
    amountPlusMargin: DollarsSchema(),
    pitch: NonEmptyStringSchema(),
    score: BidScoreSchema,
    submitted: z.object({
      userId: UserIdSchema,
      timestamp: TimestampSchema,
      agreements: AgreementIdSchema.array(),
      amount: BidAmountSchema,
    }),
    accepted: z.object({
      userId: UserIdSchema,
      timestamp: TimestampSchema,
      agreements: AgreementIdSchema.array(),
    }),
  })
  .partial({
    amount: true,
    amountPlusMargin: true,
    pitch: true,
    submitted: true,
    accepted: true,
    score: true,
  })

export type BidDomainSelectModel = ZodSimplify<
  typeof BidDomainSelectModelSchema
>

/**
 * BidDomainInsertModel
 */
export const BidDomainInsertModelSchema = BidDomainSelectModelSchema.pick({
  bidId: true,
  projectId: true,
  agencyOrganizationId: true,
  agencyTeamId: true,
  costMarginPercent: true,
})
  .extend({ status: BidStatusSchema.extract(['draft', 'declined']) })
  .partial({ bidId: true })

export type BidDomainInsertModel = ZodSimplify<
  typeof BidDomainInsertModelSchema
>

/**
 * BidDomainUpdateModel
 */
export const BidDomainUpdateModelSchema = BidDomainSelectModelSchema.pick({
  status: true,
  revision: true,
  agencyTeamId: true,
})
  .extend({
    amount: BidDomainSelectModelSchema.shape.amount.unwrap().nullable(),
    pitch: BidDomainSelectModelSchema.shape.pitch.unwrap().nullable(),
    submitted: BidDomainSelectModelSchema.shape.submitted.unwrap().nullable(),
    accepted: BidDomainSelectModelSchema.shape.accepted.unwrap().nullable(),
    score: BidScoreSchema.nullable(),
    scorecard: BidScorecardModelSchema.nullable(),
  })
  .partial()

export type BidDomainUpdateModel = ZodSimplify<
  typeof BidDomainUpdateModelSchema
>

/**
 * BidDomainSelectModelFilters
 */
export const BidDomainSelectModelFiltersSchema = z
  .object({
    bidId: ModelFilterSchema(BidIdSchema),
    bidUrn: ModelFilterSchema(BidUrnSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    agencyOrganizationId: ModelFilterSchema(OrganizationIdSchema),
    agencyTeamId: ModelFilterSchema(TeamIdSchema),
    status: ModelFilterSchema(BidStatusSchema),
  })
  .partial()

export type BidDomainSelectModelFilters = ZodSimplify<
  typeof BidDomainSelectModelFiltersSchema
>
